import React, { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'

// Toast types
export type ToastVariant = 'success' | 'error' | 'warning' | 'info'
export type ToastPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'

export interface Toast {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration?: number // ms, 0 for persistent
  dismissible?: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => string
  removeToast: (id: string) => void
  clearAll: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

// Toast Provider
interface ToastProviderProps {
  children: React.ReactNode
  position?: ToastPosition
  maxToasts?: number
}

export function ToastProvider({
  children,
  position = 'top-right',
  maxToasts = 5
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast: Toast = {
      id,
      duration: 5000,
      dismissible: true,
      ...toast
    }

    setToasts(prev => {
      const updated = [newToast, ...prev]
      return updated.slice(0, maxToasts)
    })

    return id
  }, [maxToasts])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const clearAll = useCallback(() => {
    setToasts([])
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAll }}>
      {children}
      <ToastContainer toasts={toasts} position={position} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}

// Hook to use toast
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }

  const { addToast, removeToast, clearAll } = context

  return {
    toast: addToast,
    dismiss: removeToast,
    clearAll,
    // Convenience methods
    success: (title: string, description?: string) =>
      addToast({ title, description, variant: 'success' }),
    error: (title: string, description?: string) =>
      addToast({ title, description, variant: 'error' }),
    warning: (title: string, description?: string) =>
      addToast({ title, description, variant: 'warning' }),
    info: (title: string, description?: string) =>
      addToast({ title, description, variant: 'info' })
  }
}

// Toast Container
interface ToastContainerProps {
  toasts: Toast[]
  position: ToastPosition
  removeToast: (id: string) => void
}

function ToastContainer({ toasts, position, removeToast }: ToastContainerProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 0)
    return () => clearTimeout(timer)
  }, [])

  if (!mounted) return null

  const positionClasses: Record<ToastPosition, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
  }

  const isTop = position.startsWith('top')

  return createPortal(
    <div
      className={cn(
        "fixed z-[100] flex flex-col gap-2 pointer-events-none max-w-sm w-full",
        positionClasses[position],
        isTop ? 'flex-col' : 'flex-col-reverse'
      )}
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => removeToast(toast.id)}
          index={index}
          isTop={isTop}
        />
      ))}
    </div>,
    document.body
  )
}

// Individual Toast Item
interface ToastItemProps {
  toast: Toast
  onDismiss: () => void
  index: number
  isTop: boolean
}

function ToastItem({ toast, onDismiss, index, isTop }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  const [progress, setProgress] = useState(100)

  // Define handleDismiss BEFORE it's used in useEffect
  const handleDismiss = useCallback(() => {
    setIsLeaving(true)
    setTimeout(onDismiss, 200) // Wait for exit animation
  }, [onDismiss])

  // Animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  // Auto-dismiss with progress bar
  useEffect(() => {
    if (toast.duration === 0) return

    const duration = toast.duration || 5000
    const startTime = Date.now()
    const interval = 16 // ~60fps

    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)

      if (remaining <= 0) {
        clearInterval(progressTimer)
        handleDismiss()
      }
    }, interval)

    return () => clearInterval(progressTimer)
  }, [toast.duration, handleDismiss])

  // Variant styles and icons
  const variantConfig: Record<ToastVariant, { icon: React.ReactNode; colors: string }> = {
    success: {
      icon: <CheckCircle className="w-5 h-5" />,
      colors: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
    },
    error: {
      icon: <AlertCircle className="w-5 h-5" />,
      colors: 'border-red-500/30 bg-red-500/10 text-red-400'
    },
    warning: {
      icon: <AlertTriangle className="w-5 h-5" />,
      colors: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400'
    },
    info: {
      icon: <Info className="w-5 h-5" />,
      colors: 'border-blue-500/30 bg-blue-500/10 text-blue-400'
    }
  }

  const config = variantConfig[toast.variant]

  // Progress bar colors
  const progressColors: Record<ToastVariant, string> = {
    success: 'bg-emerald-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500'
  }

  return (
    <div
      role="alert"
      className={cn(
        "pointer-events-auto relative overflow-hidden",
        "glass-panel rounded-xl border shadow-2xl",
        "transition-all duration-200 ease-out",
        "backdrop-blur-xl",
        isVisible && !isLeaving
          ? "translate-x-0 opacity-100"
          : isTop
            ? "-translate-y-2 opacity-0"
            : "translate-y-2 opacity-0",
        isLeaving && "translate-x-full opacity-0"
      )}
      style={{
        transitionDelay: `${index * 50}ms`
      }}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon */}
        <div className={cn("flex-shrink-0 mt-0.5", config.colors)}>
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white">
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-1 text-sm text-rsb-text-secondary line-clamp-2">
              {toast.description}
            </p>
          )}
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick()
                handleDismiss()
              }}
              className={cn(
                "mt-2 text-sm font-medium transition-colors",
                "hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent rounded",
                config.colors
              )}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        {/* Dismiss button */}
        {toast.dismissible !== false && (
          <button
            onClick={handleDismiss}
            className={cn(
              "flex-shrink-0 p-1 rounded-lg transition-all duration-200",
              "text-rsb-text-secondary hover:text-white hover:bg-white/10",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rsb-accent"
            )}
            aria-label="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      {toast.duration !== 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/5">
          <div
            className={cn(
              "h-full transition-all duration-100 ease-linear",
              progressColors[toast.variant]
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  )
}

// Export for standalone usage without context
export function ToastStandalone({
  toast,
  onDismiss
}: {
  toast: Toast
  onDismiss: () => void
}) {
  return (
    <ToastItem
      toast={toast}
      onDismiss={onDismiss}
      index={0}
      isTop={true}
    />
  )
}
